<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';
import { useDebounce } from '@vueuse/core';
import { useDataTableFilters } from '@DataTableModule';

const props = withDefaults(
  defineProps<{
    customClass?: string;
    debounceTime?: number;
    /** Filters a list of its own, not the data table: the table's page is left alone. */
    isLocalList?: boolean;
    loading?: boolean;
    placeholder?: string;
  }>(),
  {
    debounceTime: 350,
    placeholder: 'common.search',
  }
);

const { page } = useDataTableFilters();

const inputId = useId();

const search = defineModel<string>();

const _search = ref(search.value || '');
const _debounceSearch = useDebounce(_search, props.debounceTime);

// NOTE :: The spinner the old IconField drew while the list loads; it moves left when the clear "×" is showing.
// The wrapper does the centering: `pi-spin` animates `transform`, so a translate on the icon itself is overridden.
const spinnerClasses = computed(() => [
  'pointer-events-none absolute top-1/2 flex -translate-y-1/2 text-gray-400',
  _search.value ? 'right-9' : 'right-3',
]);

watch(_debounceSearch, (newValue) => {
  const value = newValue ?? '';
  const trimmed = value.trim();
  const startsWithNumber = /^\d/.test(trimmed);

  // Non-numeric searches: only emit when 2+ characters; otherwise clear.
  // Numeric searches: emit immediately (even 1 character).
  if (startsWithNumber) return (search.value = value);

  search.value = trimmed.length >= 2 ? value : '';
});
watch(search, () => (_search.value = search.value as string));
watch(_search, onSearchInput);

function onSearchInput() {
  if (props.isLocalList) return;

  page.value = 1;
}
</script>

<template>
  <FloatLabel :class="['relative', customClass]">
    <InputText
      v-model="_search"
      :id="inputId"
      :pt:root="[
        _search ? 'border-warning-500' : '',
        'h-9 p-1 pl-3 pr-8 text-sm w-full sm:max-w-56 2xl:max-w-none',
        customClass,
      ]"
    />

    <label
      class="-mt-2.5 flex items-center gap-1 text-sm text-zinc-500"
      :for="inputId"
    >
      <i class="pi pi-search text-sm" />

      <span>{{ $t(placeholder) }}</span>
    </label>

    <span
      v-if="loading"
      :class="spinnerClasses"
    >
      <i class="pi pi-spin pi-spinner text-sm" />
    </span>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="scale-75 opacity-0"
      leave-active-class="transition duration-150 ease-in"
      leave-to-class="scale-75 opacity-0"
    >
      <Button
        v-if="_search"
        rounded
        aria-label="Cancel"
        icon="pi pi-times"
        pt:icon="text-[10px] font-semibold"
        pt:root="absolute right-2 top-1/2 size-5 -translate-y-1/2 border-gray-400 bg-gray-400 p-0"
        @click="_search = ''"
      />
    </Transition>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="scale-0 opacity-0"
      leave-active-class="transition duration-150 ease-in"
      leave-to-class="scale-0 opacity-0"
    >
      <i
        v-if="_search"
        class="pi pi-circle-fill absolute right-[-6px] top-[-4px] text-sm text-warning-500"
      />
    </Transition>
  </FloatLabel>
</template>
